// @ts-nocheck
import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { ReactComponent as CloseIcon } from '../../../../assets/Icons/lucideIcons/x.svg';
import {
  Box,
  Button,
  Card,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Typography,
} from '@mui/material';

const IMPACT_EMPTY_STATE_CARD_SX = {
  padding: 4,
  textAlign: 'center',
  width: '100%',
};

const IMPACT_ATTRIBUTE_CARD_SX = {
  padding: 2,
};

const IMPACT_CHIP_CONTAINER_SX = {
  display: 'flex',
  flexWrap: 'wrap',
  gap: 1,
};

/**
 * ImpactAnalysisModal — Shows impact analysis results during Change Request workflow
 * Displays which attributes will be impacted (derived or auto-populated) by the proposed changes.
 * Modal shows empty state if no attributes are impacted.
 *
 * @component
 * @param {Object} props - Component props
 * @param {boolean} props.open - Controls modal visibility
 * @param {Function} props.onClose - Callback to close the modal
 * @param {Object} props.impactedData - Map of attribute IDs to impact data
 *   Each entry: { attributeId: { label, name, impact_derived[], impact_autopopulate[] } }
 * @returns {React.ReactElement} Dialog showing impact analysis results
 */
const ImpactAnalysisModal = ({ open, onClose, impactedData }) => {
  /**
   * Filters and memoizes impacted attributes that have derived or auto-populated impacts.
   * @type {Array<[string, Object]>} Array of [attributeId, impactData] entries with impacts
   */
  const impactedAttributes = useMemo(() => {
    return Object.entries(impactedData || {}).filter(([, impactData]) => {
      const hasDerived = (impactData?.impact_derived || [])?.length > 0;
      const hasAutoPopulate =
        (impactData?.impact_autopopulate || [])?.length > 0;
      return hasDerived || hasAutoPopulate;
    });
  }, [impactedData]);

  /**
   * Determines if there are no impacted attributes to display.
   * @type {boolean} True if empty state should be shown
   */
  const hasNoImpactedAttributes = useMemo(() => {
    return impactedAttributes?.length === 0;
  }, [impactedAttributes]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      aria-labelledby="impact-result-modal"
    >
      <DialogTitle id="impact-result-modal">
        <Box component="span">Impact Analysis Result</Box>
      </DialogTitle>
      <IconButton
        color="primary"
        aria-label="Close impact modal"
        title="Close"
        onClick={onClose}
        sx={{
          position: 'absolute',
          right: 16,
          top: 12,
        }}
      >
        <CloseIcon />
      </IconButton>
      <DialogContent dividers>
        <Grid container spacing={2}>
          {!hasNoImpactedAttributes ? (
            <Grid size={12}>
              <Typography variant="subtitle1">Impacted Attributes</Typography>
            </Grid>
          ) : null}

          {hasNoImpactedAttributes ? (
            <Card sx={IMPACT_EMPTY_STATE_CARD_SX}>
              <Typography
                variant="body1"
                sx={{
                  color: 'text.secondary',
                }}
              >
                No attributes will be impacted by these changes.
              </Typography>
            </Card>
          ) : (
            <Grid container rowSpacing={2} size={12}>
              {impactedAttributes.map(([attributeId, impactData]) => {
                const hasDerived =
                  (impactData?.impact_derived || [])?.length > 0;
                const hasAutoPopulate =
                  (impactData?.impact_autopopulate || [])?.length > 0;

                return (
                  <Grid key={attributeId} size={12}>
                    <Card sx={IMPACT_ATTRIBUTE_CARD_SX} variant="outlined">
                      <Grid container rowSpacing={2} size={12}>
                        <Grid size={12}>
                          <Typography variant="subtitle4">
                            {impactData?.label || 'Attribute'}
                          </Typography>
                        </Grid>

                        <Grid container rowSpacing={1} size={12}>
                          {hasDerived && (
                            <>
                              <Grid size={12}>
                                <Typography variant="body2">
                                  Derived Attributes:
                                </Typography>
                              </Grid>
                              <Grid sx={IMPACT_CHIP_CONTAINER_SX} size={12}>
                                {impactData?.impact_derived?.map((attr) => (
                                  <Chip
                                    key={`derived-${attributeId}-${attr?.id}`}
                                    label={attr?.label}
                                    title={`${attr?.label} (${attr?.name})`}
                                    color="primary"
                                    size="small"
                                  />
                                ))}
                              </Grid>
                            </>
                          )}
                        </Grid>

                        <Grid container rowSpacing={1} size={12}>
                          {hasAutoPopulate && (
                            <>
                              <Grid size={12}>
                                <Typography variant="body2">
                                  Auto-populated Attributes:
                                </Typography>
                              </Grid>
                              <Grid sx={IMPACT_CHIP_CONTAINER_SX} size={12}>
                                {impactData?.impact_autopopulate?.map(
                                  (attr) => (
                                    <Chip
                                      key={`autopopulate-${attributeId}-${attr?.id}`}
                                      title={`${attr?.label} (${attr?.name})`}
                                      label={attr?.label}
                                      color="secondary"
                                      size="small"
                                    />
                                  )
                                )}
                              </Grid>
                            </>
                          )}
                        </Grid>
                      </Grid>
                    </Card>
                  </Grid>
                );
              })}
            </Grid>
          )}
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} variant="outlined" title="Close">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

ImpactAnalysisModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  impactedData: PropTypes.shape({}),
};

ImpactAnalysisModal.defaultProps = {
  impactedData: {},
};

export default ImpactAnalysisModal;
